/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { 
  X, 
  Truck, 
  Maximize2, 
  Minimize2, 
  FileText, 
  Printer,
  ChevronRight,
  Navigation,
  Layers,
  MapPin
} from 'lucide-react';
import { Folio, CostCenter, Destination, isClientUser, isClientOperatorUser, shouldHidePricesForUser } from '../types.ts';
import { dataService } from '../lib/dataService.ts';
import { downloadFolioPDF } from '../utils/pdfGenerator.ts';
import LogisticsMap from './LogisticsMap.tsx';

interface FolioRealtimeMapModalProps {
  folio?: Folio;
  foliosList?: Folio[];
  costCenters?: CostCenter[];
  destinations?: Destination[];
  onClose: () => void;
  onOpenDetails?: (folio: Folio) => void;
  currentUser?: { role?: string; clientCode?: string; assignedClientIds?: string[] } | null;
  isClientViewOnly?: boolean;
}

export default function FolioRealtimeMapModal({
  folio: initialFolio,
  foliosList = [],
  costCenters = [],
  destinations: destinationsProps,
  onClose,
  onOpenDetails,
  currentUser,
  isClientViewOnly
}: FolioRealtimeMapModalProps) {
  const isUserClient = isClientUser(currentUser) || isClientOperatorUser(currentUser) || shouldHidePricesForUser(currentUser) || Boolean(isClientViewOnly);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'map' | 'details' | 'split'>('map');
  const [allDestinations, setAllDestinations] = useState<Destination[]>(destinationsProps || []);

  const activeFolios = useMemo(() => {
    if (foliosList && foliosList.length > 0) return foliosList;
    if (initialFolio) return [initialFolio];
    return [];
  }, [foliosList, initialFolio]);

  const folio = activeFolios[0] || ({ id: 'N/A', clientName: 'Sin cliente', status: 'SALIDA_CEDIS', items: [] } as unknown as Folio);

  useEffect(() => {
    if (destinationsProps && destinationsProps.length > 0) {
      setAllDestinations(destinationsProps);
      return;
    }
    const unsub = dataService.subscribeToDestinations((items) => {
      setAllDestinations(items);
    });
    return () => unsub();
  }, [destinationsProps]);

  // Resolve target destination coordinates for this folio
  const resolvedDestination = useMemo(() => {
    // 1. Direct coordinates on folio
    if (folio.destinationLat && folio.destinationLng) {
      return {
        lat: Number(folio.destinationLat),
        lng: Number(folio.destinationLng),
        name: folio.destination || folio.costCenterName || 'Planta Destino',
        routeNotes: folio.routeNotes,
        estimatedKm: folio.estimatedKm
      };
    }

    // 2. Match by destinationId in catalog
    if (folio.destinationId && allDestinations.length > 0) {
      const match = allDestinations.find(d => d.id === folio.destinationId);
      if (match) {
        return {
          lat: Number(match.lat),
          lng: Number(match.lng),
          name: match.name,
          routeNotes: match.routeNotes,
          estimatedKm: match.estimatedKm
        };
      }
    }

    // 3. Match by name or address keyword in catalog
    if (folio.destination && allDestinations.length > 0) {
      const match = allDestinations.find(d => 
        d.name.toLowerCase().includes(folio.destination!.toLowerCase()) ||
        d.address.toLowerCase().includes(folio.destination!.toLowerCase())
      );
      if (match) {
        return {
          lat: Number(match.lat),
          lng: Number(match.lng),
          name: match.name,
          routeNotes: match.routeNotes,
          estimatedKm: match.estimatedKm
        };
      }
    }

    // 4. Match in costCenters
    if (costCenters && costCenters.length > 0) {
      const ccMatch = costCenters.find(cc => 
        cc.id === folio.costCenterId ||
        cc.name.toLowerCase() === (folio.costCenterName || '').toLowerCase() ||
        cc.name.toLowerCase().includes((folio.destination || '').toLowerCase())
      );
      if (ccMatch && ccMatch.lat && ccMatch.lng) {
        return {
          lat: Number(ccMatch.lat),
          lng: Number(ccMatch.lng),
          name: ccMatch.name,
          routeNotes: folio.routeNotes,
          estimatedKm: folio.estimatedKm
        };
      }
    }

    // 5. Keyword fallback for known industrial areas
    if (folio.destination) {
      const lower = folio.destination.toLowerCase();
      if (lower.includes('bmw') || lower.includes('reyes') || lower.includes('san luis')) {
        return { lat: 21.9724, lng: -100.8537, name: 'BMW PLANTA - VILLA DE REYES' };
      }
      if (lower.includes('saltillo')) {
        return { lat: 25.4316, lng: -101.0062, name: 'Planta Saltillo' };
      }
      if (lower.includes('ramos')) {
        return { lat: 25.5420, lng: -100.9310, name: 'Planta Ramos Arizpe' };
      }
      if (lower.includes('puebla')) {
        return { lat: 19.0437, lng: -98.1980, name: 'Planta Puebla' };
      }
      if (lower.includes('apodaca')) {
        return { lat: 25.7800, lng: -100.1800, name: 'Planta Apodaca' };
      }
      if (lower.includes('santa catarina')) {
        return { lat: 25.6700, lng: -100.4600, name: 'Planta Santa Catarina' };
      }
    }

    return {
      lat: 25.4316,
      lng: -101.0062,
      name: folio.destination || folio.costCenterName || 'Planta Destino'
    };
  }, [folio, allDestinations, costCenters]);

  // Construct previewDestination for LogisticsMap
  const previewDest = useMemo(() => {
    return {
      name: resolvedDestination.name,
      address: folio.destination || resolvedDestination.name,
      lat: resolvedDestination.lat,
      lng: resolvedDestination.lng,
      originLat: folio.originLat ? Number(folio.originLat) : 25.7820,
      originLng: folio.originLng ? Number(folio.originLng) : -100.4050,
      originName: folio.origin || '🏢 CEDIS Escobedo',
      routeNotes: resolvedDestination.routeNotes || folio.routeNotes,
      estimatedKm: resolvedDestination.estimatedKm || folio.estimatedKm
    };
  }, [resolvedDestination, folio]);

  const totalPieces = useMemo(() => {
    return folio.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [folio.items]);

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col justify-end sm:justify-center items-center p-0 sm:p-4 bg-slate-950/85 backdrop-blur-md overflow-hidden animate-in fade-in duration-200">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 15 }}
        className={`bg-slate-900 text-white rounded-t-3xl sm:rounded-3xl border-t sm:border border-slate-700/80 shadow-2xl w-full flex flex-col overflow-hidden transition-all duration-300 ${
          isFullscreen ? 'fixed inset-0 sm:inset-2 z-[99999] rounded-none sm:rounded-2xl' : 'max-w-6xl h-[94vh] sm:h-[92vh]'
        }`}
      >
        {/* Mobile grab handle */}
        <div className="bg-[#18181c] pt-2 pb-0 flex justify-center sm:hidden shrink-0">
          <div className="w-12 h-1 bg-white/20 rounded-full" />
        </div>

        {/* MODAL HEADER */}
        <div className="bg-[#18181c] border-b border-slate-800 pt-[calc(env(safe-area-inset-top,0px)+8px)] sm:pt-4 px-4 sm:px-5 pb-3 sm:pb-4 flex items-center justify-between gap-3 shrink-0 sticky top-0 z-30 shadow-md">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div className="p-2 sm:p-2.5 bg-red-600/20 border border-red-500/40 rounded-xl text-red-500 shrink-0">
              <Truck className="h-4 w-4 sm:h-5 sm:w-5 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[9px] font-mono font-black uppercase rounded-md flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                  MONITOREO GPS
                </span>
                <span className="text-[10px] sm:text-[11px] text-slate-400 font-mono font-bold truncate">
                  {folio.clientName}
                </span>
              </div>
              <h2 className="text-sm sm:text-lg font-black text-white font-mono mt-0.5 truncate flex items-center gap-2">
                FOLIO #{folio.id}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {onOpenDetails && (
              <button
                onClick={() => onOpenDetails(folio)}
                className="hidden sm:flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-slate-700 cursor-pointer"
              >
                <FileText className="h-4 w-4 text-slate-400" />
                <span>Ficha</span>
              </button>
            )}

            <button
              onClick={() => downloadFolioPDF(folio, { hidePrices: isUserClient })}
              className="bg-red-700 hover:bg-red-600 text-white text-xs font-bold px-2.5 sm:px-3 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Descargar Remisión PDF"
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">PDF</span>
            </button>

            <button
              onClick={onClose}
              aria-label="Cerrar mapa en tiempo real"
              className="h-10 w-10 sm:h-9 sm:w-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center cursor-pointer border border-white/20 shrink-0 transition-all shadow-xs"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* MOBILE NAVIGATION TABS (< lg) */}
        <div className="lg:hidden bg-slate-950 border-b border-slate-800 p-2 flex items-center justify-center gap-1.5 shrink-0 z-10">
          <button
            type="button"
            onClick={() => setMobileTab('map')}
            className={`flex-1 py-2 px-2 rounded-xl text-xs font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              mobileTab === 'map'
                ? 'bg-red-600 text-white shadow-md border border-red-500'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            <Navigation className="h-3.5 w-3.5 text-amber-400" />
            <span>🗺️ Mapa Live</span>
          </button>

          <button
            type="button"
            onClick={() => setMobileTab('details')}
            className={`flex-1 py-2 px-2 rounded-xl text-xs font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              mobileTab === 'details'
                ? 'bg-red-600 text-white shadow-md border border-red-500'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            <FileText className="h-3.5 w-3.5 text-blue-400" />
            <span>📋 Telemetría</span>
          </button>

          <button
            type="button"
            onClick={() => setMobileTab('split')}
            className={`flex-1 py-2 px-2 rounded-xl text-xs font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              mobileTab === 'split'
                ? 'bg-red-600 text-white shadow-md border border-red-500'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            <Layers className="h-3.5 w-3.5 text-emerald-400" />
            <span>📱 Dividido</span>
          </button>
        </div>

        {/* MAP & TELEMETRY BODY */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-4 overflow-hidden relative">
          
          {/* LOGISTICS MAP CANVAS (3 cols on desktop) */}
          <div className={`relative bg-slate-950 overflow-hidden ${
            mobileTab === 'map'
              ? 'flex-1 h-full w-full lg:col-span-3'
              : mobileTab === 'split'
              ? 'h-[45vh] min-h-[300px] sm:h-[380px] w-full shrink-0 border-b border-slate-800 lg:col-span-3 lg:h-full'
              : 'hidden lg:block lg:col-span-3 lg:h-full'
          }`}>
            <LogisticsMap
              folios={activeFolios}
              costCenters={costCenters.filter(cc => !folio.clientId || cc.clientId === folio.clientId)}
              currentUser={{
                uid: 'system',
                email: 'sistema@isn.com',
                name: 'Sistema Logístico',
                role: 'admin',
                permissions: { canCreateFolios: false, canUpdateStages: false, canViewDashboard: true, canManageUsers: false, canBilling: false }
              }}
              destinations={allDestinations.filter(d => !folio.clientId || d.clientId === folio.clientId)}
              hideSidebarConsole={true}
              previewDestination={previewDest}
            />
          </div>

          {/* RIGHT TELEMETRY & FOLIO CONSOLE SIDEBAR (1 col on desktop) */}
          <div className={`bg-[#141418] border-l border-slate-800 p-4 sm:p-5 text-slate-300 text-xs ${
            mobileTab === 'details'
              ? 'flex-1 h-full w-full overflow-y-auto space-y-5 lg:col-span-1'
              : mobileTab === 'split'
              ? 'flex-1 w-full overflow-y-auto space-y-5 lg:col-span-1'
              : 'hidden lg:block lg:col-span-1 lg:h-full lg:overflow-y-auto space-y-5'
          }`}>
            
            {/* RUTA DE TRANSPORTE (ORIGEN ➔ DESTINO) */}
            <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-4 space-y-3 shadow-inner">
              <span className="text-[10px] font-black uppercase tracking-wider text-blue-400 font-mono block">
                🗺️ RUTA Y TRAYECTO LOGÍSTICO
              </span>

              <div className="space-y-2">
                <div className="bg-slate-900/90 p-2.5 rounded-xl border border-blue-500/30">
                  <span className="text-[9px] text-blue-400 font-mono font-bold uppercase block">📍 Origen (Punto de Partida):</span>
                  <p className="font-extrabold text-white text-xs mt-0.5">
                    {folio.origin || '🏢 CEDIS Escobedo - Monterrey NL'}
                  </p>
                </div>

                <div className="flex justify-center text-slate-500 font-mono text-[10px]">
                  <span>⬇️ Trayecto en Ruta GPS ⬇️</span>
                </div>

                <div className="bg-slate-900/90 p-2.5 rounded-xl border border-red-500/30">
                  <span className="text-[9px] text-red-400 font-mono font-bold uppercase block">🏁 Destino (Punto de Entrega):</span>
                  <p className="font-extrabold text-white text-xs mt-0.5">
                    {folio.destination || resolvedDestination.name}
                  </p>
                </div>
              </div>
            </div>

            {/* DRIVER & VEHICLE BOX */}
            <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-4 space-y-3 shadow-inner">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono block">
                🚚 UNIDAD Y CHOFER
              </span>

              <div className="space-y-2">
                <div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase block">Operador Asignado:</span>
                  <p className="font-extrabold text-white text-sm mt-0.5">
                    {folio.driverName || 'Operador Logístico ISN'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-700/60 font-mono text-xs">
                  <div>
                    <span className="text-[9px] text-slate-400 block uppercase">N° Económico:</span>
                    <span className="font-bold text-emerald-400">{folio.economicNumber || 'TRK-NL'}</span>
                  </div>
                  <div>
                    <span className="text-[9px] text-slate-400 block uppercase">Estatus Folio:</span>
                    <span className="font-bold text-amber-400 uppercase">{folio.status.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CARGO / MATERIAL SUMMARY */}
            <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-700/60 pb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">
                  📦 CARGA EN TRÁNSITO
                </span>
                <span className="bg-red-500/20 text-red-400 border border-red-500/40 text-[10px] font-bold px-2 py-0.5 rounded-md font-mono">
                  {totalPieces} PZAS
                </span>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {folio.items.map((it, idx) => (
                  <div key={idx} className="bg-slate-900/80 p-2.5 rounded-xl border border-slate-800 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-white text-xs truncate">{it.description}</p>
                      <span className="text-[9px] text-slate-500 font-mono block">ID: {it.goodsItemId}</span>
                    </div>
                    <span className="font-mono font-bold text-red-400 text-xs shrink-0 bg-red-950/60 px-2 py-0.5 rounded-md border border-red-900/50">
                      {it.quantity} PZAS
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ROUTE TIMELINE LOGS */}
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono block">
                ⏱️ EVENTOS DE CERTIFICACIÓN
              </span>

              <div className="space-y-2">
                {folio.history.map((h, idx) => (
                  <div key={idx} className="bg-slate-900/60 p-2.5 rounded-xl border border-slate-800/80 text-[11px] space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-white uppercase text-[10px] font-mono">
                        ✓ {h.stage.replace(/_/g, ' ')}
                      </span>
                      <span className="text-[9px] text-slate-500 font-mono">
                        {new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400">Operador: {h.operatorName}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FOOTER ACTIONS IN SIDEBAR */}
            <div className="pt-3 border-t border-slate-800 space-y-2">
              {onOpenDetails && (
                <button
                  onClick={() => onOpenDetails(folio)}
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border border-slate-700 text-xs"
                >
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span>Ver Ficha Completa de Folio</span>
                </button>
              )}

              <button
                onClick={onClose}
                className="w-full bg-red-700 hover:bg-red-600 text-white font-extrabold py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
              >
                Cerrar Monitoreo
              </button>
            </div>

          </div>

        </div>
      </motion.div>
    </div>
  );
}
